import { Tabs as MuiTabs } from "@mui/material";
import { type AriaAttributes, type ReactElement } from "react";

import { Tab, type TabProps } from "./Tab";

type TabChild<Value extends string> = ReactElement<TabProps<Value>, typeof Tab>;

export type TabsProps<Value extends string> = AriaAttributes & {
  value: Value;
  children: TabChild<Value> | ReadonlyArray<TabChild<Value> | null> | null;
  onChange: (value: Value) => void;
  className?: string;
};

export function Tabs<Value extends string>({
  value,
  children,
  onChange,
  ...props
}: TabsProps<Value>) {
  return (
    <MuiTabs
      variant="fullWidth"
      value={value}
      onChange={(_, newValue) => {
        if (typeof newValue !== "string") {
          return;
        }

        onChange(newValue as Value);
      }}
      {...props}
      sx={{
        backgroundColor: "var(--color-surface-neutral-medium)",
        borderRadius: "var(--radius-xl)",
        padding: "calc(var(--spacing)*2)",
        minHeight: "unset",
        width: "100%",
        "& .MuiTabs-indicator": {
          height: "100%",
          zIndex: 0,
          backgroundColor: "var(--color-white)",
          borderRadius: "var(--radius-lg)",
        },
        "& .MuiTabs-list": {
          position: "relative",
          backgroundColor: "transparent",
          zIndex: 1,
        },
      }}
    >
      {children}
    </MuiTabs>
  );
}
